import React, { Component, ReactNode } from "react";
import { RotateCcw, AlertCircle } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "../../ui/alert";
import { Button } from "../../ui/button";

interface Props {
  children: ReactNode;
  onReset?: () => void;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component for document cropper functionality
 * Catches JavaScript errors in child components and displays a fallback UI
 * Particularly useful for catching OpenCV.js and jscanify runtime errors
 */
export class CropperErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error for debugging in development only
    if (import.meta.env.DEV) {
      console.error("CropperErrorBoundary caught an error:", error, errorInfo);
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const {
        fallbackMessage = "Ocurrió un error inesperado en el editor de documentos.",
      } = this.props;

      return (
        <div className="p-4">
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertTitle>Error en el editor de documentos</AlertTitle>
            <AlertDescription>
              <p className="mb-2">{fallbackMessage}</p>
              {this.state.error && (
                <p className="text-xs text-muted-foreground font-mono mb-3">
                  {this.state.error.message}
                </p>
              )}
              <Button variant="outline" size="sm" onClick={this.handleReset}>
                <RotateCcw className="size-4" />
                Reintentar
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}
